import { useState, useEffect } from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutoExpandingTextarea } from "@/components/auto-expanding-textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { CalendarIcon, Trash2, Archive, Target, ListTodo, GripVertical, ChevronDown, ChevronUp, Share2 } from "lucide-react";
import { format, startOfWeek, endOfWeek, addWeeks, addMonths, addYears, startOfQuarter } from "date-fns";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent, DragOverEvent, useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { formatDateInput, parseLocalDate } from "@/lib/dateUtils";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { PlannerItem, Category } from "@shared/schema";
import ShareDialog from "@/components/share-dialog";

type Section = "today" | "week" | "nextweek" | "month" | "nextmonth" | "quarter" | "year" | "nextyear" | "future";

const SECTIONS: Array<{ value: Section; label: string }> = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "nextweek", label: "Next Week" },
  { value: "month", label: "This Month" },
  { value: "nextmonth", label: "Next Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
  { value: "nextyear", label: "Next Year" },
  { value: "future", label: "Future" },
];

function getSectionDateLabel(section: Section): string {
  const now = new Date();
  
  switch (section) {
    case "today":
      return format(now, "MMMM d, yyyy");
    case "week": {
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${format(weekStart, "MMM d")}-${format(weekEnd, "d, yyyy")}`;
      }
      return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;
    }
    case "nextweek": {
      const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      if (nextWeekStart.getMonth() === nextWeekEnd.getMonth()) {
        return `${format(nextWeekStart, "MMM d")}-${format(nextWeekEnd, "d, yyyy")}`;
      }
      return `${format(nextWeekStart, "MMM d")} - ${format(nextWeekEnd, "MMM d, yyyy")}`;
    }
    case "month":
      return format(now, "MMMM yyyy");
    case "nextmonth":
      return format(addMonths(now, 1), "MMMM yyyy");
    case "quarter": {
      const quarterStart = startOfQuarter(now);
      const quarterNum = Math.floor(quarterStart.getMonth() / 3) + 1;
      return `Q${quarterNum} ${format(now, "yyyy")}`;
    }
    case "year":
      return format(now, "yyyy");
    case "nextyear":
      return format(addYears(now, 1), "yyyy");
    case "future":
      return "";
    default:
      return "";
  }
}

interface DroppableSectionProps {
  section: Section;
  children: React.ReactNode;
}

function DroppableSection({ section, children }: DroppableSectionProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: section,
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[200px] p-4 rounded-lg transition-colors",
        isOver ? "bg-muted/50 border-2 border-primary" : "bg-muted/30"
      )}
      data-testid={`section-${section}`}
    >
      {children}
    </div>
  );
}

interface SortablePlannerItemProps {
  item: PlannerItem;
  categories: Category[];
  onUpdate: (id: string, data: Partial<PlannerItem>) => void;
  onDelete: (id: string) => void;
  onShare: (id: string) => void;
  onConvertToGoal: (id: string) => void;
  onConvertToTask: (id: string) => void;
}

function SortablePlannerItem({
  item,
  categories,
  onUpdate,
  onDelete,
  onShare,
  onConvertToGoal,
  onConvertToTask,
}: SortablePlannerItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [text, setText] = useState(item.text);

  useEffect(() => {
    setText(item.text);
  }, [item.text]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  const handleTextBlur = () => {
    if (text !== item.text) {
      onUpdate(item.id, { text });
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-3 bg-card border rounded-lg mb-2"
      data-testid={`planner-item-${item.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="mt-2 cursor-grab active:cursor-grabbing"
        data-testid={`drag-handle-${item.id}`}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </button>

      <div className="flex-1 space-y-2">
        <AutoExpandingTextarea
          value={text}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          className="w-full min-h-[2.5rem]"
          minRows={1}
          placeholder="Enter your plan..."
          data-testid={`input-text-${item.id}`}
        />

        <div className="flex items-center gap-2 flex-wrap">
          {/* Category Selector */}
          <Select
            value={item.categoryId || "none"}
            onValueChange={(value) => onUpdate(item.id, { categoryId: value === "none" ? null : value })}
          >
            <SelectTrigger className="w-40" data-testid={`select-category-${item.id}`}>
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Category</SelectItem>
              <CategorySelectGroups
                categories={categories}
                getValue={(c) => String(c.id)}
                renderContent={(c) => (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color ?? undefined }} />
                    {c.name}
                  </div>
                )}
              />
            </SelectContent>
          </Select>

          {/* Due Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "min-w-[180px] justify-start text-left font-normal",
                  !item.dueDate && "text-muted-foreground"
                )}
                data-testid={`button-due-date-${item.id}`}
              >
                <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                {item.dueDate ? format(new Date(item.dueDate), "PPP") : "Due date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={item.dueDate ? new Date(item.dueDate) : undefined}
                onSelect={(date) => onUpdate(item.id, { dueDate: date ? formatDateInput(date) : null } as any)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* Actions */}
          <div className="flex items-center gap-1 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onConvertToGoal(item.id)}
              title="Convert to Goal"
              data-testid={`button-convert-goal-${item.id}`}
            >
              <Target className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onConvertToTask(item.id)}
              title="Convert to Task"
              data-testid={`button-convert-task-${item.id}`}
            >
              <ListTodo className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUpdate(item.id, { status: "archived" })}
              title="Archive"
              data-testid={`button-archive-${item.id}`}
            >
              <Archive className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onShare(item.id)}
              title="Share"
              data-testid={`button-share-${item.id}`}
            >
              <Share2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(item.id)}
              title="Delete"
              data-testid={`button-delete-${item.id}`}
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PlannerPage() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");
  const [statsCollapsed, setStatsCollapsed] = useState<boolean>(false);
  const [sharingPlanner, setSharingPlanner] = useState<PlannerItem | null>(null);

  // Load from user-scoped key once userId is known.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(userLsKey("planner-stats-collapsed", userId));
      if (saved !== null) setStatsCollapsed(saved === "true");
      if (userId) localStorage.removeItem("planner-stats-collapsed");
    } catch {}
  }, [userId]);

  const toggleStats = () => {
    setStatsCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(userLsKey("planner-stats-collapsed", userId), String(next)); } catch {}
      return next;
    });
  };

  const { data: items = [] } = useQuery<PlannerItem[]>({
    queryKey: ["/api/planner"],
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: { text: string; section: Section }) => {
      const response = await apiRequest("POST", "/api/planner", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/planner"] });
    },
    onError: () => {
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PlannerItem> }) => {
      const response = await apiRequest("PATCH", `/api/planner/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/planner"] });
    },
    onError: () => {
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/planner/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/planner"] });
    },
    onError: () => {
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (itemOrders: Array<{ id: string; displayOrder: number; section: string }>) => {
      await apiRequest("PATCH", "/api/planner/reorder", { itemOrders });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/planner"] });
    },
    onError: () => {
    },
  });

  const [convertDialog, setConvertDialog] = useState<{
    open: boolean;
    type: "goal" | "task" | null;
    itemId: string | null;
    categoryId: string | null;
    dueDate: string | null;
  }>({ open: false, type: null, itemId: null, categoryId: null, dueDate: null });

  const openConvertDialog = (id: string, type: "goal" | "task") => {
    const item = items.find((i) => i.id === id);
    setConvertDialog({
      open: true,
      type,
      itemId: id,
      categoryId: item?.categoryId ?? null,
      dueDate: item?.dueDate != null ? String(item.dueDate) : null,
    });
  };

  const closeConvertDialog = () =>
    setConvertDialog({ open: false, type: null, itemId: null, categoryId: null, dueDate: null });

  const convertToGoalMutation = useMutation({
    mutationFn: async ({ id, categoryId, dueDate }: { id: string; categoryId: string | null; dueDate: string | null }) => {
      const response = await apiRequest("POST", `/api/planner/${id}/convert-to-goal`, { categoryId, dueDate });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/planner"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
    },
  });

  const convertToTaskMutation = useMutation({
    mutationFn: async ({ id, categoryId, dueDate }: { id: string; categoryId: string | null; dueDate: string | null }) => {
      const response = await apiRequest("POST", `/api/planner/${id}/convert-to-task`, { categoryId, dueDate });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/planner"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
    },
    onError: () => {
    },
  });

  const handleConvertConfirm = () => {
    if (!convertDialog.itemId || !convertDialog.type) return;
    const payload = { id: convertDialog.itemId, categoryId: convertDialog.categoryId, dueDate: convertDialog.dueDate };
    if (convertDialog.type === "goal") {
      convertToGoalMutation.mutate(payload);
    } else {
      convertToTaskMutation.mutate(payload);
    }
    closeConvertDialog();
  };

  const convertDialogValid = !!convertDialog.categoryId && !!convertDialog.dueDate;

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const filteredItems = getFilteredItems();
    const activeItem = filteredItems.find((item) => item.id === active.id);
    if (!activeItem) return;

    // Check if dropped over a section (droppable) or an item (sortable)
    const overItem = filteredItems.find((item) => item.id === over.id);
    
    let newSection: Section;
    let insertIndex = 0;

    if (overItem) {
      // Dropped over another item - use filtered items only
      newSection = overItem.section as Section;
      const sectionItems = filteredItems
        .filter((item) => item.section === newSection)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      insertIndex = sectionItems.findIndex((item) => item.id === overItem.id);
    } else {
      // Dropped over a section droppable area
      const sectionId = over.id.toString();
      if (!SECTIONS.some(s => s.value === sectionId)) return;
      newSection = sectionId as Section;
      const sectionItems = filteredItems
        .filter((item) => item.section === newSection)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      insertIndex = sectionItems.length; // Add to end if dropped on empty section
    }

    // If item is already in this section at this position, no need to update
    if (activeItem.section === newSection) {
      const sectionItems = filteredItems
        .filter((item) => item.section === newSection)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      const currentIndex = sectionItems.findIndex((item) => item.id === activeItem.id);
      if (currentIndex === insertIndex || currentIndex + 1 === insertIndex) {
        return;
      }
    }

    // Get all items in the target section from ALL items (not just filtered)
    // We need to reorder ALL items in the section to maintain proper displayOrder
    const allSectionItems = items
      .filter((item) => item.section === newSection && item.id !== activeItem.id && item.status !== "archived")
      .sort((a, b) => a.displayOrder - b.displayOrder);

    // Find where to insert in the full list based on filtered position
    const filteredSectionItems = filteredItems
      .filter((item) => item.section === newSection && item.id !== activeItem.id)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    
    const targetItem = filteredSectionItems[insertIndex];
    let fullListInsertIndex = targetItem 
      ? allSectionItems.findIndex((item) => item.id === targetItem.id)
      : allSectionItems.length;

    // Insert the active item at the correct position in the full list
    const reorderedItems = [...allSectionItems];
    reorderedItems.splice(fullListInsertIndex, 0, activeItem);

    // Create update payload with new display orders for ALL items in section
    const itemOrders = reorderedItems.map((item, index) => ({
      id: item.id,
      displayOrder: index,
      section: newSection,
    }));

    reorderMutation.mutate(itemOrders);
  };

  const getItemsBySection = (section: Section) => {
    return items
      .filter((item) => {
        if (item.section !== section || item.status === "archived") return false;
        if (selectedCategoryFilter === "all") return true;
        if (selectedCategoryFilter === "uncategorized") return !item.categoryId;
        return item.categoryId === selectedCategoryFilter;
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  };

  const handleAddItem = (section: Section) => {
    createMutation.mutate({ text: "", section });
  };

  const getSectionStats = () => {
    return SECTIONS.map((section) => ({
      label: section.label,
      value: section.value,
      count: items.filter((item) => {
        if (item.section !== section.value || item.status === "archived") return false;
        if (selectedCategoryFilter === "all") return true;
        if (selectedCategoryFilter === "uncategorized") return !item.categoryId;
        return item.categoryId === selectedCategoryFilter;
      }).length,
    }));
  };

  // Get all filtered items (respects category filter)
  const getFilteredItems = () => {
    return items.filter((item) => {
      if (item.status === "archived") return false;
      if (selectedCategoryFilter === "all") return true;
      if (selectedCategoryFilter === "uncategorized") return !item.categoryId;
      return item.categoryId === selectedCategoryFilter;
    });
  };

  return (
    <div className="flex-1 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      <div className="w-full space-y-6 md:space-y-8 px-4 md:px-0">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2" data-testid="text-planner-title">Planner</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Organize your plans across different time horizons
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Filter by Category:</label>
          <Select
            value={selectedCategoryFilter}
            onValueChange={setSelectedCategoryFilter}
          >
            <SelectTrigger className="w-64" data-testid="select-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="uncategorized">Uncategorized</SelectItem>
              <CategorySelectGroups
                categories={categories}
                getValue={(c) => String(c.id)}
                renderContent={(c) => (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color ?? undefined }} />
                    {c.name}
                  </div>
                )}
              />
            </SelectContent>
          </Select>
        </div>

        {/* Stats Dashboard */}
        <div>
          <button
            onClick={toggleStats}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-3 select-none"
          >
            {statsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            {statsCollapsed ? "Show overview" : "Hide overview"}
          </button>
          {!statsCollapsed && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {getSectionStats().map((stat) => (
                <div
                  key={stat.value}
                  className="bg-card border rounded-lg p-4 shadow-sm"
                  data-testid={`stat-card-${stat.value}`}
                >
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    {stat.label}
                    {getSectionDateLabel(stat.value as Section) && (
                      <span className="block text-xs mt-0.5">
                        ({getSectionDateLabel(stat.value as Section)})
                      </span>
                    )}
                  </div>
                  <div className="text-3xl font-bold" data-testid={`stat-count-${stat.value}`}>
                    {stat.count}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stat.count === 1 ? 'plan' : 'plans'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={getFilteredItems().map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-6">
              {SECTIONS.map((section) => {
                const sectionItems = getItemsBySection(section.value);
                
                return (
                  <div key={section.value} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold" data-testid={`text-section-${section.value}`}>
                        {section.label}
                        {getSectionDateLabel(section.value) && (
                          <span className="text-muted-foreground font-normal ml-2">
                            ({getSectionDateLabel(section.value)})
                          </span>
                        )}
                      </h2>
                      <Button
                        onClick={() => handleAddItem(section.value)}
                        size="sm"
                        data-testid={`button-add-${section.value}`}
                      >
                        Add Item
                      </Button>
                    </div>

                    <DroppableSection section={section.value}>
                      {sectionItems.map((item) => (
                        <SortablePlannerItem
                          key={item.id}
                          item={item}
                          categories={categories}
                          onUpdate={(id, data) => updateMutation.mutate({ id, data })}
                          onDelete={(id) => deleteMutation.mutate(id)}
                          onShare={(id) => setSharingPlanner(item)}
                          onConvertToGoal={(id) => openConvertDialog(id, "goal")}
                          onConvertToTask={(id) => openConvertDialog(id, "task")}
                        />
                      ))}

                      {sectionItems.length === 0 && (
                        <div className="text-center text-muted-foreground py-8">
                          No items yet. Click "Add Item" to get started.
                        </div>
                      )}

                      {sectionItems.length > 0 && (
                        <div className="flex justify-end pt-2">
                          <Button
                            onClick={() => handleAddItem(section.value)}
                            size="sm"
                            variant="outline"
                            data-testid={`button-add-bottom-${section.value}`}
                          >
                            Add Item
                          </Button>
                        </div>
                      )}
                    </DroppableSection>
                  </div>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Convert to Goal / Task dialog */}
      <Dialog open={convertDialog.open} onOpenChange={(open) => { if (!open) closeConvertDialog(); }}>
        <DialogContent >
          <DialogHeader>
            <DialogTitle>
              {convertDialog.type === "goal" ? "Move to Goal" : "Move to Task"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Category — required */}
            <div className="space-y-1.5">
              <Label>
                Category <span className="text-destructive">*</span>
              </Label>
              <Select
                value={convertDialog.categoryId || ""}
                onValueChange={(v) => setConvertDialog((d) => ({ ...d, categoryId: v || null }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <CategorySelectGroups
                    categories={categories}
                    getValue={(c) => String(c.id)}
                    renderContent={(c) => (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color ?? undefined }} />
                        {c.name}
                      </div>
                    )}
                  />
                </SelectContent>
              </Select>
              {!convertDialog.categoryId && (
                <p className="text-xs text-muted-foreground">A category is required.</p>
              )}
            </div>

            {/* Due Date — required */}
            <div className="space-y-1.5">
              <Label>
                Due Date <span className="text-destructive">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !convertDialog.dueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {convertDialog.dueDate
                      ? format(new Date(convertDialog.dueDate), "PPP")
                      : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={convertDialog.dueDate ? new Date(convertDialog.dueDate) : undefined}
                    onSelect={(date) =>
                      setConvertDialog((d) => ({ ...d, dueDate: date ? formatDateInput(date) : null }))
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {!convertDialog.dueDate && (
                <p className="text-xs text-muted-foreground">A due date is required.</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeConvertDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleConvertConfirm}
              disabled={!convertDialogValid || convertToGoalMutation.isPending || convertToTaskMutation.isPending}
            >
              {convertDialog.type === "goal" ? "Create Goal" : "Create Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sharingPlanner && (
        <ShareDialog
          isOpen={!!sharingPlanner}
          onClose={() => setSharingPlanner(null)}
          itemId={sharingPlanner.id}
          itemType="planner_item"
          itemTitle={sharingPlanner.text.substring(0, 30)}
        />
      )}
    </div>
  );
}
